import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs/operators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { Toast } from '@/shared/components/toast/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CardContainer } from '@/shared/components/card-container/card-container';
import { TableViewSwitcherComponent } from '@/shared/components/layout/table-view-switcher/table-view-switcher.component';
import { LayoutService } from '@/layout/service/layout.service';
import { MotivoAfastamentoService } from '@/core/api/sicom-api/sistema/motivo-afastamento/motivo-afastamento.service';
import { MotivoAfastamentoResponse } from '@/core/api/sicom-api/sistema/motivo-afastamento/motivo-afastamento.model';
import { MotivoAfastamentoDetalhe } from './componentes/motivo-afastamento-detalhe/motivo-afastamento-detalhe';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { FiltroCampo } from '@/shared/components/layout/filtros-bar/filtro-campo';
import { FiltrosBar } from '@/shared/components/layout/filtros-bar/filtros-bar';
import { CardSortPills, CardSortOption } from '@/shared/components/layout/card-sort-pills/card-sort-pills';

interface SortOpcao {
    label: string;
    field: 'descricao' | 'codigo';
    direction: 'asc' | 'desc';
}

@Component({
    selector: 'app-motivo-afastamento',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        TableModule,
        PaginatorModule,
        SelectModule,
        InputTextModule,
        Toast,
        ConfirmDialogModule,
        TooltipModule,
        CardContent,
        CardContainer,
        TableViewSwitcherComponent,
        MotivoAfastamentoDetalhe,
        CanDirective,
        FiltrosBar,
        FiltroCampo,
        CardSortPills
    ],
    providers: [MessageService, ConfirmationService],
    templateUrl: './motivo-afastamento.html',
    styleUrl: './motivo-afastamento.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
/** Cadastro (lista + modal) de motivos de afastamento de usuário, com filtro/ordenação client-side e exclusão. */
export class MotivoAfastamento implements OnInit {
    private readonly service = inject(MotivoAfastamentoService);
    private readonly layoutService = inject(LayoutService);
    private readonly messageService = inject(MessageService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly destroyRef = inject(DestroyRef);

    protected readonly roleMotivoEditar = PERMISSOES.configuracoes.motivoAfastamento.editar;
    protected readonly roleMotivoExcluir = PERMISSOES.configuracoes.motivoAfastamento.excluir;

    protected readonly motivos = signal<MotivoAfastamentoResponse[]>([]);
    protected readonly carregando = signal(false);

    // ── Visualização, filtro e ordenação (padrão refis) ──
    protected readonly currentView = signal<'table' | 'card'>(this.layoutService.getPresentationType());
    protected readonly filtroDescricao = signal('');
    protected readonly filtroAplicado = signal('');
    protected readonly sortField = signal<SortOpcao['field']>('descricao');
    protected readonly sortOrder = signal<1 | -1>(1);

    /**
     * Critérios das pills do modo card. Um por campo: a direção vem do clique na pill,
     * e não de duas opções separadas como no seletor que havia aqui.
     */
    protected readonly sortOpcoes: CardSortOption<SortOpcao['field']>[] = [
        { field: 'descricao', label: 'Descrição' },
        { field: 'codigo', label: 'Código' }
    ];

    protected readonly motivosView = computed<MotivoAfastamentoResponse[]>(() => {
        const termo = this.filtroAplicado().trim().toLowerCase();
        const field = this.sortField();
        const order = this.sortOrder();

        const filtrados = termo ? this.motivos().filter((m) => (m.descricao ?? '').toLowerCase().includes(termo)) : [...this.motivos()];

        return filtrados.sort((a, b) => {
            const va = a[field];
            const vb = b[field];
            let cmp: number;
            if (typeof va === 'number' && typeof vb === 'number') {
                cmp = va - vb;
            } else {
                cmp = String(va ?? '').localeCompare(String(vb ?? ''), 'pt-BR');
            }
            return cmp * order;
        });
    });

    // ── Paginação ──
    protected readonly first = signal(0);
    protected readonly rows = signal(10);
    protected readonly totalRecords = computed(() => this.motivosView().length);
    protected readonly motivosPagina = computed<MotivoAfastamentoResponse[]>(() => {
        const inicio = this.first();
        return this.motivosView().slice(inicio, inicio + this.rows());
    });

    // ── Modal (componente separado) ──
    protected readonly modalAberto = signal(false);
    protected readonly motivoEditando = signal<MotivoAfastamentoResponse | null>(null);

    ngOnInit(): void {
        this.carregar();
    }

    protected onPageChange(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? this.rows());
    }

    // ── Filtros / ordenação (client-side; só o botão "Filtrar" aplica o texto) ──
    protected aplicarFiltros(): void {
        this.filtroAplicado.set(this.filtroDescricao());
        this.first.set(0);
    }

    protected limparFiltros(): void {
        this.filtroDescricao.set('');
        this.filtroAplicado.set('');
        this.sortField.set(this.sortOpcoes[0].field);
        this.sortOrder.set(1);
        this.first.set(0);
    }

    /** Trocar a ordenação volta para a primeira página: a página atual pode nem existir depois. */
    protected onSortCardChange(): void {
        this.first.set(0);
    }

    protected onSortChange(event: { field?: string | string[] | null; order?: number | null }): void {
        const field = (Array.isArray(event.field) ? event.field[0] : event.field) as SortOpcao['field'] | undefined;
        if (!field) return;
        const order: 1 | -1 = event.order === -1 ? -1 : 1;
        this.sortField.set(field);
        this.sortOrder.set(order);
        this.first.set(0);
    }

    // ── CRUD ──
    protected abrirNovo(): void {
        this.motivoEditando.set(null);
        this.modalAberto.set(true);
    }

    protected abrirEditar(motivo: MotivoAfastamentoResponse): void {
        this.motivoEditando.set(motivo);
        this.modalAberto.set(true);
    }

    /** Após salvar no modal, recarrega a lista. */
    protected onSalvo(): void {
        this.carregar();
    }

    protected confirmarExclusao(motivo: MotivoAfastamentoResponse): void {
        this.confirmationService.confirm({
            header: 'Remover motivo',
            message: `Deseja realmente remover o motivo "${motivo.descricao}"?`,
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Remover',
            rejectLabel: 'Cancelar',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => this.excluir(motivo.codigo)
        });
    }

    private excluir(codigo: number): void {
        this.service
            .excluir(codigo)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: () => {
                    this.motivos.update((lista) => lista.filter((m) => m.codigo !== codigo));
                    if (this.first() >= this.totalRecords()) {
                        this.first.set(Math.max(0, this.totalRecords() - this.rows()));
                    }
                    this.messageService.add({ severity: 'success', summary: 'Sucesso', detail: 'Motivo removido com sucesso.' });
                },
                error: () => undefined
            });
    }

    private carregar(): void {
        this.carregando.set(true);
        this.service
            .listar()
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (lista) => {
                    this.motivos.set(lista ?? []);
                    this.first.set(0);
                },
                error: () => this.motivos.set([])
            });
    }
}
